import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { GoalSearchIcon } from "@/apps/goals/assets/icons";
import { RegionConfirmModal } from "@/apps/goals/components/RegionConfirmModal/RegionConfirmModal";
import { useGoalsStore } from "@/apps/goals/store/goalsStore";
import { RegionGroup } from "@/apps/goals/types";
import { getGoalsScreenUrl } from "@/apps/goals/utils/goalsRoutes";
import { useStartupParamsStore } from "@/store/startupParams";
import Button from "@/ui/Button/Button";
import Typography from "@/ui/Typography/Typography";
import cls from "./styles.module.scss";

export const RegionSelectorPage = () => {
  const { t } = useTranslation();
  const regionGroups = useGoalsStore(
    (state) => state.initData?.region_groups ?? []
  );
  const selectRegion = useGoalsStore((state) => state.selectRegion);
  const yandexId = useStartupParamsStore((state) => state.auth.yandexId);
  const [loadingRegionGroupId, setLoadingRegionGroupId] = useState<
    number | null
  >(null);
  const [selectedRegionGroup, setSelectedRegionGroup] =
    useState<RegionGroup | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const selectedRegionName = selectedRegionGroup
    ? selectedRegionGroup.display_name ?? selectedRegionGroup.name
    : "";
  const filteredRegionGroups = useMemo(() => {
    const normalizedSearchQuery = searchQuery.trim().toLowerCase();

    if (!normalizedSearchQuery) return regionGroups;

    return regionGroups.filter((regionGroup) => {
      const regionName = regionGroup.name.toLowerCase();
      const displayName = regionGroup.display_name?.toLowerCase() ?? "";

      return (
        regionName.includes(normalizedSearchQuery) ||
        displayName.includes(normalizedSearchQuery)
      );
    });
  }, [regionGroups, searchQuery]);

  const closeConfirmModal = () => {
    if (loadingRegionGroupId !== null) return;

    setSelectedRegionGroup(null);
  };

  const confirmSelectedRegion = async () => {
    if (!yandexId || !selectedRegionGroup || loadingRegionGroupId !== null) {
      return;
    }

    setLoadingRegionGroupId(selectedRegionGroup.id);

    try {
      const screen = await selectRegion(yandexId, selectedRegionGroup.id);
      setSelectedRegionGroup(null);
      navigate({
        pathname: getGoalsScreenUrl(screen),
        search: location.search,
      });
    } finally {
      setLoadingRegionGroupId(null);
    }
  };

  return (
    <>
      <section className={cls.root}>
        <Typography component="h1" size="2xl" weight="bold" family="custom">
          {t("goals.regionSelector.title")}
        </Typography>
        <label className={cls.searchField}>
          <span className={cls.searchIcon}>
            <GoalSearchIcon />
          </span>
          <input
            className={cls.searchInput}
            type="search"
            placeholder="Найти свой регион"
            value={searchQuery}
            disabled={loadingRegionGroupId !== null}
            onChange={(event) => setSearchQuery(event.target.value)}
          />
        </label>
        <div className={cls.regionList}>
          {filteredRegionGroups.map((regionGroup) => (
            <Button
              key={regionGroup.id}
              className={cls.regionButton}
              label={regionGroup.display_name ?? regionGroup.name}
              variant="light"
              loading={loadingRegionGroupId === regionGroup.id}
              disabled={loadingRegionGroupId !== null}
              onClick={() => setSelectedRegionGroup(regionGroup)}
            />
          ))}
        </div>
      </section>

      <RegionConfirmModal
        cityName={selectedRegionName}
        isOpen={selectedRegionGroup !== null}
        isLoading={loadingRegionGroupId === selectedRegionGroup?.id}
        onConfirm={confirmSelectedRegion}
        onCancel={closeConfirmModal}
      />
    </>
  );
};
